import { useEffect } from 'react';
import { Form, Input, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

export type DramaActionMode = 'approve' | 'reject' | 'offline';

type Props = {
    open: boolean;
    mode: DramaActionMode;
    title: React.ReactNode;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: { reason: string }) => void;
};

const ReviewModal: React.FC<Props> = ({ open, mode, title, loading, onCancel, onSubmit }) => {
    const [form] = Form.useForm<{ reason: string }>();
    const { formatMessage } = useIntl();
    const isOffline = mode === 'offline';
    const isApprove = mode === 'approve';

    useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, mode, form]);

    const okTextId = isOffline
        ? 'dramaReview.offline.submit'
        : isApprove
          ? 'dramaReview.approve.submit'
          : 'dramaReview.reject.submit';

    const reasonLabelId = isOffline
        ? 'dramaReview.offline.reason'
        : isApprove
          ? 'dramaReview.approve.reason'
          : 'dramaReview.reject.reason';

    const reasonPlaceholderId = isOffline
        ? 'dramaReview.offline.reasonPlaceholder'
        : isApprove
          ? 'dramaReview.approve.reasonPlaceholder'
          : 'dramaReview.reject.reasonPlaceholder';

    return (
        <Modal
            open={open}
            title={title}
            destroyOnClose
            confirmLoading={loading}
            okText={formatMessage({ id: okTextId })}
            okButtonProps={isApprove || isOffline ? undefined : { danger: true }}
            cancelText={formatMessage({ id: 'common.cancel' })}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form form={form} layout="vertical" onFinish={onSubmit}>
                <Form.Item
                    name="reason"
                    label={<FormattedMessage id={reasonLabelId} />}
                    rules={
                        isApprove
                            ? []
                            : [
                                  {
                                      required: true,
                                      message: formatMessage({
                                          id: isOffline
                                              ? 'dramaReview.offline.reasonRequired'
                                              : 'dramaReview.reject.reasonRequired',
                                      }),
                                  },
                                  ...(isOffline
                                      ? [
                                            {
                                                max: 200,
                                                message: formatMessage({
                                                    id: 'dramaReview.offline.reasonMax',
                                                }),
                                            },
                                        ]
                                      : []),
                              ]
                    }
                >
                    <Input.TextArea
                        rows={4}
                        maxLength={isOffline ? 200 : undefined}
                        showCount={isOffline}
                        placeholder={formatMessage({ id: reasonPlaceholderId })}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ReviewModal;
